import React, { useEffect, useRef, useState } from "react";
import Modal from "./Modal";
import { createShare, getShareInfo } from "../api/client";
import "./ShareModal.css";

function ShareModal({ projectId, onClose }) {
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [generating, setGenerating] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [sharedUsers, setSharedUsers] = useState([]);
  const [sharedUsersLoading, setSharedUsersLoading] = useState(true);
  const [sharedUsersError, setSharedUsersError] = useState("");
  const [sharedUsersOpen, setSharedUsersOpen] = useState(false);
  const [copiedShareId, setCopiedShareId] = useState(null);
  const copyResetTimeoutRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const fetchSharedUsers = async () => {
      setSharedUsersLoading(true);
      setSharedUsersError("");
      try {
        const shares = await getShareInfo(projectId);
        if (isMounted) {
          setSharedUsers(shares || []);
        }
      } catch (err) {
        if (isMounted) {
          setSharedUsersError(
            err.response?.data?.message ||
              err.response?.data?.error ||
              "Failed to load shared users",
          );
        }
      } finally {
        if (isMounted) {
          setSharedUsersLoading(false);
        }
      }
    };

    fetchSharedUsers();

    return () => {
      isMounted = false;
    };
  }, [projectId]);

  useEffect(() => {
    return () => {
      if (copyResetTimeoutRef.current) {
        clearTimeout(copyResetTimeoutRef.current);
      }
    };
  }, []);

  const toggleSharedUsers = () => {
    setSharedUsersOpen((prev) => !prev);
  };

  const buildShareLink = (share) => {
    if (typeof window === "undefined") {
      return null;
    }
    if (!share?.share_token) {
      return null;
    }
    const baseLink = `${window.location.origin}/projects/${projectId}?share_token=${share.share_token}`;
    const dataParam = share?.data ? `&data=${encodeURIComponent(share.data)}` : "";
    return `${baseLink}${dataParam}`;
  };

  const getShareUserInfo = (share) => {
    if (share?.user) {
      return share.user;
    }

    if (
      share?.data &&
      typeof window !== "undefined" &&
      typeof window.atob === "function"
    ) {
      try {
        return JSON.parse(window.atob(share.data));
      } catch (parseError) {
        return null;
      }
    }

    return null;
  };

  const markShareCopied = (shareId) => {
    if (!shareId) {
      return;
    }
    setCopiedShareId(shareId);
    if (copyResetTimeoutRef.current) {
      clearTimeout(copyResetTimeoutRef.current);
    }
    copyResetTimeoutRef.current = setTimeout(() => {
      setCopiedShareId(null);
    }, 3000);
  };

  const handleCopyShareLink = async (share) => {
    const link = buildShareLink(share);
    if (!link) {
      return;
    }
    try {
      await navigator.clipboard.writeText(link);
      markShareCopied(share.id);
    } catch (err) {
      alert("Failed to copy to clipboard");
    }
  };

  const handleGenerateLink = async (e) => {
    e.preventDefault();
    setError("");
    setGenerating(true);

    try {
      const share = await createShare(projectId, {
        user_name: userName,
        user_email: userEmail || undefined,
      });

      const link = `${window.location.origin}/projects/${projectId}?share_token=${share.share_token}&data=${encodeURIComponent(share.data)}`;
      setShareLink(link);
      setSharedUsersOpen(true);
      setSharedUsers((prevShares) => {
        if (!share) {
          return prevShares;
        }
        const existingIndex = prevShares.findIndex(
          (item) => item.id === share.id,
        );
        if (existingIndex !== -1) {
          const updated = [...prevShares];
          updated[existingIndex] = share;
          return updated;
        }
        return [share, ...prevShares];
      });

      // Auto copy to clipboard
      await navigator.clipboard.writeText(link);
      setCopied(true);
      markShareCopied(share.id);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to generate share link");
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      alert("Failed to copy to clipboard");
    }
  };

  return (
    <Modal onClose={onClose} title="Share Project">
      <div className="share-modal-content">
        {error && <div className="error-message">{error}</div>}

        <section className="shared-users-section">
          <button
            type="button"
            className={`shared-users-toggle ${sharedUsersOpen ? "open" : ""}`}
            onClick={toggleSharedUsers}
            aria-expanded={sharedUsersOpen}
          >
            <div className="shared-users-header">
              <h3 className="shared-users-title">Shared Users</h3>
              <span className="shared-users-count">
                {sharedUsersLoading ? "--" : sharedUsers.length}
              </span>
            </div>
            <span
              className={`shared-users-chevron ${sharedUsersOpen ? "open" : ""}`}
              aria-hidden="true"
            ></span>
          </button>
          {sharedUsersOpen && (
            <div className="shared-users-panel">
              {sharedUsersLoading ? (
                <div className="shared-users-status">Loading shared users...</div>
              ) : sharedUsersError ? (
                <div className="error-message">{sharedUsersError}</div>
              ) : sharedUsers.length === 0 ? (
                <div className="shared-users-empty">
                  No users have access yet. Generate a share link to invite someone.
                </div>
              ) : (
                <ul className="shared-users-list">
                  {sharedUsers.map((share) => {
                    const userDetails = getShareUserInfo(share);
                    const name = userDetails?.name || "Unnamed user";
                    const email = userDetails?.email;
                    const sharedAt = share?.created_at
                      ? new Date(share.created_at)
                      : null;
                    const formattedSharedAt =
                      sharedAt && !Number.isNaN(sharedAt.getTime())
                        ? sharedAt.toLocaleString()
                        : null;
                    const shareLink = buildShareLink(share);
                    const isCopied = copiedShareId === share.id;

                    return (
                      <li className="shared-users-item" key={share.id}>
                        <div className="shared-user-main">
                          <div className="shared-user-text">
                            <span className="shared-user-name">{name}</span>
                            {email && (
                              <span className="shared-user-email">{email}</span>
                            )}
                            {formattedSharedAt && (
                              <div className="shared-user-meta">
                                Shared on {formattedSharedAt}
                              </div>
                            )}
                          </div>
                          {shareLink && (
                            <button
                              type="button"
                              className="btn btn-secondary btn-xs"
                              onClick={() => handleCopyShareLink(share)}
                            >
                              {isCopied ? "Copied!" : "Copy Link"}
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </section>

        {!shareLink ? (
          <form onSubmit={handleGenerateLink} id="share-form" name="share-form">
            <div className="form-group">
              <label htmlFor="share-user-name" className="form-label">
                User Name *
              </label>
              <input
                type="text"
                id="share-user-name"
                name="share-user-name"
                className="input"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="Enter user name"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="share-user-email" className="form-label">
                User Email (Optional)
              </label>
              <input
                type="email"
                id="share-user-email"
                name="share-user-email"
                className="input"
                value={userEmail}
                onChange={(e) => setUserEmail(e.target.value)}
                placeholder="Enter user email"
              />
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={generating}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn"
                disabled={generating}
                id="generate-link-button"
                name="generate-link-button"
              >
                {generating && <span className="spinner"></span>}
                {generating ? "Generating..." : "Generate Link"}
              </button>
            </div>
          </form>
        ) : (
          <div className="share-result">
            <div className="success-message">
              Link generated and copied to clipboard!
            </div>
            <div className="share-link-container">
              <input
                type="text"
                className="input share-link-input"
                value={shareLink}
                readOnly
                id="share-link"
                name="share-link"
              />
              <button
                className="btn"
                onClick={handleCopyLink}
                id="copy-link-button"
                name="copy-link-button"
              >
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
            <div className="share-info">
              <p>
                Share this link with <strong>{userName}</strong> to allow them to
                view and participate.
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default ShareModal;
